/*
    The rules of the menu's moving smoke (scripts/menuSmoke.ts draws it), kept apart from the page so they can be
    tested: where the picture lies on the screen, whether the smoke may move, and when a frame is drawn.
*/

/** the menu background's size: the smoke's fires and everything else are in its pixels */
export const SMOKE_PICTURE = { width: 2560, height: 1440 } as const;

export interface SmokeFire {
    /** where the fire is, in the picture's pixels */
    readonly x: number
    readonly y: number
    /** how big the fire and its plume are (1: the usual) */
    readonly size: number
    /** how bright its light is */
    readonly heat: number
    /** how far its smoke goes before it has thinned out (pixels, times its size) */
    readonly reach: number
    /** how much smoke it gives (1: the usual) */
    readonly smoke: number
}

/** the five fires of the picture */
export const SMOKE_FIRES: readonly SmokeFire[] = [
    { x: 1036.8, y: 1224, size: 1.3, heat: 1.2, reach: 1500, smoke: 1 },   // the refinery
    { x: 1605.12, y: 648, size: 1, heat: 1, reach: 1300, smoke: 1 },       // a house in Uptown
    { x: 2380.8, y: 230.4, size: 0.8, heat: 0.8, reach: 1200, smoke: 1 },  // another, to the north-east
    { x: 248.32, y: 1202.4, size: 0.9, heat: 0.9, reach: 1200, smoke: 1 }, // the boat on the beach
    { x: 524.8, y: 921.6, size: 0.7, heat: 0.8, reach: 900, smoke: 0.8 }   // on the sand below the prison
];

/** the way the smoke goes, in the picture (east and a little north), as a unit vector */
export const SMOKE_WIND: readonly [number, number] = (() => {
    const x = 0.86, y = -0.5, length = Math.hypot(x, y);
    return [x / length, y / length] as const;
})();

export interface PictureCover {
    /** the picture pixel at the canvas's top-left corner */
    readonly originX: number
    readonly originY: number
    /** picture pixels a canvas pixel */
    readonly scale: number
}

/**
 * Where the picture lies under a canvas of `bufferWidth` pixels across covering a box of `width` × `height` CSS pixels,
 * as `background-size: cover; background-position: center` lays it out in that box
 */
export function pictureCover(width: number, height: number, bufferWidth: number): PictureCover {
    const cover = Math.max(width / SMOKE_PICTURE.width, height / SMOKE_PICTURE.height);
    return {
        originX: -(width - SMOKE_PICTURE.width * cover) / 2 / cover,
        originY: -(height - SMOKE_PICTURE.height * cover) / 2 / cover,
        scale: width / bufferWidth / cover
    };
}

export interface SmokeSettings {
    /** the player asked their system for reduced motion */
    readonly reducedMotion: boolean
    /** the Battlefield look ("off", "low", "medium", "high") */
    readonly look: string
    /** the browser draws without a graphics card */
    readonly software: boolean
}

/** whether the smoke may move: not with reduced motion, the lightest graphics (the look off or Low) or no graphics card */
export function smokeMayMove({ reducedMotion, look, software }: SmokeSettings): boolean {
    return !reducedMotion && look !== "off" && look !== "low" && !software;
}

/** at most 30 frames a second: smoke is slow, and the menu stays light */
export const SMOKE_FRAME_MS = 1000 / 30;
/** frames further apart than `gap` (ms) for longer than `span` (ms): the device can't keep up */
export const SMOKE_SLOW = { span: 6000, gap: 70 } as const;

export type SmokeStep =
    | { readonly kind: "skip" }
    | { readonly kind: "draw", readonly time: number }
    | { readonly kind: "stop" };

/**
 * When the smoke is drawn: told of every animation frame (its time, ms, and whether the menu can be seen), says whether
 * to draw it (and at what time of its own, in seconds, 0 at its first frame), skip the frame, or stop for good
 */
export class SmokePacer {
    private _begun?: number;
    private _last = 0;
    private _slowSince = 0;

    frame(now: number, visible: boolean): SmokeStep {
        // hidden (a match on, or the tab): nothing drawn, and the frame rate is judged afresh after
        if (!visible) {
            this._last = 0;
            this._slowSince = 0;
            return { kind: "skip" };
        }
        // the first frame, or a clock that went back (which a browser's doesn't): the smoke's own clock starts at 0
        if (this._begun === undefined || now < this._begun || now < this._last) {
            this._begun = now;
            this._last = 0;
            this._slowSince = 0;
        }
        if (this._last !== 0) {
            const gap = now - this._last;
            if (gap < SMOKE_FRAME_MS - 2) return { kind: "skip" };
            if (gap > SMOKE_SLOW.gap) {
                this._slowSince ||= now;
                if (now - this._slowSince > SMOKE_SLOW.span) return { kind: "stop" };
            } else {
                this._slowSince = 0;
            }
        }
        this._last = now;
        return { kind: "draw", time: (now - this._begun) / 1000 };
    }
}
